import { useState, useMemo } from "react";
import { format, parseISO } from "date-fns";
import { nb } from "date-fns/locale";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarGroup } from "./ProjectAvatar";
import { SortableHeader } from "@/components/shared/SortableHeader";
import type { UserLookup } from "@/hooks/use-users";
import type { ProjectHours } from "@/hooks/use-project-hours";
import type { ProjectStorage } from "@/hooks/use-project-storage";
import type { DBProject } from "@/lib/db-types";
import { formatNOK, formatStorage } from "@/lib/format";
import { cn } from "@/lib/utils";
import { computeCompletedTotals, type CompletedAgg } from "@/lib/completed-projects";
import {
  type SortKey,
  type SortDir,
  type CompletedRow,
  nextSortState,
  toCompletedRow,
  sortCompletedRows,
} from "./project-sort";

/**
 * VIB-1189: avsluttet-variant av prosjektlista. Fokus på lønnsomhet/leveranse:
 * BF reell, endelig timepris, solgt vs. brukt og budsjettavvik — med en vektet
 * summeringslinje nederst. Egen komponent så ProjectTable (aktiv) holdes ren.
 *
 * VIB-2518: sorteringen deles med ProjectTable via project-sort.ts (samme
 * SortKey-union, samme toggle-regel) og kan styres utenfra, slik at
 * ProjectsPage kan bære sorteringen i URL på tvers av status-filteret.
 */

interface Row {
  id: string;
  code: string | null;
  name: string;
  salesIds: string[];
  advisorId: string | null;
  plId: string | null;
  bfActual: number;
  timerSolgt: number;
  timerBrukt: number;
  timepris: number | null;
  avvik: number | null;
  dato: string | null;
  /** VIB-2103: lagret datamengde + lokasjon, fra prosjektets storage-avtale. */
  storageGb: number | null;
  storageLocation: string | null;
}

function fmtDato(val: string | null): string {
  if (!val) return "—";
  try {
    return format(parseISO(val), "d. MMM yyyy", { locale: nb });
  } catch {
    return "—";
  }
}

function fmtTimer(h: number): string {
  return Math.round(h).toLocaleString("nb-NO");
}

function fmtRate(r: number | null): string {
  return r == null ? "—" : `${Math.round(r).toLocaleString("nb-NO")} kr/t`;
}

/** Budsjettavvik med fortegn + farge (positiv = over budsjett = rødt). */
function AvvikCell({ pct }: { pct: number | null }) {
  if (pct == null) return <span className="text-muted-foreground">—</span>;
  const rounded = Math.round(pct);
  return (
    <span className={cn("tabular-nums font-medium", rounded > 0 ? "text-red-600" : "text-green-600")}>
      {rounded > 0 ? "+" : ""}{rounded} %
    </span>
  );
}

export function CompletedProjectsTable({
  title, projects, ownersMap, userMap, hoursMap, storageMap, navigate, activeProjectId,
  sortKey: controlledSortKey, sortDir: controlledSortDir, onToggleSort,
}: {
  title: string;
  projects: DBProject[];
  ownersMap: Map<string, string[]>;
  userMap: Map<string, UserLookup>;
  hoursMap: Map<string, ProjectHours>;
  /** VIB-2103: project_id → lagret datamengde. Tom map = kolonnen viser «—». */
  storageMap?: Map<string, ProjectStorage>;
  navigate: (path: string) => void;
  activeProjectId?: string | null;
  /**
   * Controlled sortering: når onToggleSort er satt styres sorteringen utenfra
   * (ProjectsPage persisterer den over navigasjon). Ellers brukes lokal state.
   */
  sortKey?: SortKey;
  sortDir?: SortDir;
  onToggleSort?: (key: SortKey) => void;
}) {
  const [localSortKey, setLocalSortKey] = useState<SortKey>(null);
  const [localSortDir, setLocalSortDir] = useState<SortDir>("asc");
  const isControlled = !!onToggleSort;
  const sortKey = isControlled ? (controlledSortKey ?? null) : localSortKey;
  const sortDir = isControlled ? (controlledSortDir ?? "asc") : localSortDir;

  const toggleSort = (key: SortKey) => {
    if (isControlled) {
      onToggleSort!(key);
      return;
    }
    const next = nextSortState(localSortKey, localSortDir, key);
    setLocalSortKey(next.key);
    setLocalSortDir(next.dir);
  };

  const rows: CompletedRow[] = useMemo(
    () => projects.map((p) => toCompletedRow(p, ownersMap, hoursMap, storageMap)),
    [projects, ownersMap, hoursMap, storageMap],
  );

  // VIB-2103: sum for lagringskolonnen, vist i summeringslinja nederst.
  const totalStorageGb = useMemo(
    () => rows.reduce((sum, r) => sum + (r.storageGb ?? 0), 0),
    [rows],
  );

  // VIB-2528: delt med ProjectsPage så piltastene følger vist rekkefølge.
  const sorted = useMemo(
    () => sortCompletedRows(rows, sortKey, sortDir, userMap),
    [rows, sortKey, sortDir, userMap],
  );

  const totals = useMemo(
    () => computeCompletedTotals(rows.map((r): CompletedAgg => ({ bfActual: r.bfActual, timerSolgt: r.timerSolgt, timerBrukt: r.timerBrukt }))),
    [rows],
  );

  const d = "text-sm py-2 px-3";

  return (
    <Card>
      <CardContent className="p-3 sm:p-4 md:p-6">
        <h3 className="font-display text-lg text-foreground tracking-wide px-1 sm:px-0 mb-4">{title}</h3>

        {/* Mobile cards */}
        <div className="xl:hidden space-y-3">
          {sorted.map((r) => (
            <button
              key={r.id}
              data-project-card={r.id}
              onClick={() => navigate(`/prosjekter/${r.id}`)}
              className={cn(
                "w-full text-left p-3 rounded-lg border transition-colors min-h-[48px]",
                activeProjectId === r.id ? "border-primary bg-primary/5" : "border-border/50 bg-card/50",
              )}
            >
              <p className="font-medium text-xs text-primary">{r.code}</p>
              <p className="font-medium text-sm truncate" title={r.name}>{r.name}</p>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs mt-2">
                <span className="text-muted-foreground">GEEK reell: <span className="font-medium text-foreground">{formatNOK(r.bfActual)}</span></span>
                <span className="text-muted-foreground">Timepris: <span className="font-medium text-foreground">{fmtRate(r.timepris)}</span></span>
                <span className="text-muted-foreground">Solgt: <span className="font-medium text-foreground">{fmtTimer(r.timerSolgt)} t</span></span>
                <span className="text-muted-foreground">Brukt: <span className="font-medium text-foreground">{fmtTimer(r.timerBrukt)} t</span></span>
                <span className="text-muted-foreground">Avvik: <AvvikCell pct={r.avvik} /></span>
                <span className="text-muted-foreground">Lukket: <span className="font-medium text-foreground">{fmtDato(r.dato)}</span></span>
                <span className="text-muted-foreground">Lagring: <span className="font-medium text-foreground">{formatStorage(r.storageGb)}</span>{r.storageLocation ? ` · ${r.storageLocation}` : ""}</span>
              </div>
            </button>
          ))}
          {sorted.length === 0 && <div className="py-8 text-center text-muted-foreground text-sm">Ingen prosjekter</div>}
        </div>

        {/* Desktop table */}
        <div className="hidden xl:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border text-left">
                <SortableHeader label="Kode" active={sortKey === "code"} dir={sortDir} onToggle={() => toggleSort("code")} align="left" className={cn("pl-1 w-[52px]", d)} />
                <SortableHeader label="Prosjekt" active={sortKey === "name"} dir={sortDir} onToggle={() => toggleSort("name")} align="left" className={cn("", d)} />
                <SortableHeader label="Salg" active={sortKey === "salg"} dir={sortDir} onToggle={() => toggleSort("salg")} align="center" className={cn("text-center w-[70px]", d)} />
                <SortableHeader label="Rådgiver" active={sortKey === "advisor"} dir={sortDir} onToggle={() => toggleSort("advisor")} align="center" className={cn("text-center w-[50px]", d)} />
                <SortableHeader label="PL" active={sortKey === "pl"} dir={sortDir} onToggle={() => toggleSort("pl")} align="center" className={cn("text-center w-[50px]", d)} />
                <SortableHeader label="GEEK reell" active={sortKey === "bf_actual"} dir={sortDir} onToggle={() => toggleSort("bf_actual")} align="right" className={cn("text-right w-[90px]", d)} />
                <SortableHeader label="Endelig timepris" active={sortKey === "timepris"} dir={sortDir} onToggle={() => toggleSort("timepris")} align="right" className={cn("text-right w-[90px]", d)} />
                <SortableHeader label="Timer solgt" active={sortKey === "solgt"} dir={sortDir} onToggle={() => toggleSort("solgt")} align="right" className={cn("text-right w-[70px]", d)} />
                <SortableHeader label="Timer brukt" active={sortKey === "brukt"} dir={sortDir} onToggle={() => toggleSort("brukt")} align="right" className={cn("text-right w-[70px]", d)} />
                <SortableHeader label="Budsjettavvik" active={sortKey === "avvik"} dir={sortDir} onToggle={() => toggleSort("avvik")} align="right" className={cn("text-right w-[80px]", d)} />
                <SortableHeader label="Dato lukket" active={sortKey === "dato"} dir={sortDir} onToggle={() => toggleSort("dato")} align="right" className={cn("text-right pr-1 w-[110px]", d)} />
                <SortableHeader label="Lagring" active={sortKey === "lagring"} dir={sortDir} onToggle={() => toggleSort("lagring")} align="right" className={cn("text-right pr-1 w-[110px]", d)} />
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <tr
                  key={r.id}
                  data-project-id={r.id}
                  aria-current={activeProjectId === r.id ? "true" : undefined}
                  className={cn("border-b border-border/50 cursor-pointer transition-colors", activeProjectId === r.id ? "bg-primary/5" : "hover:bg-muted/20")}
                  onClick={() => navigate(`/prosjekter/${r.id}`)}
                >
                  <td className={cn("pl-1 font-medium text-primary tabular-nums", d)}>{r.code}</td>
                  <td className={cn("pr-3", d)}><p className="font-medium text-foreground truncate max-w-[260px]">{r.name}</p></td>
                  <td className={cn("text-center", d)}><AvatarGroup userIds={r.salesIds} userMap={userMap} /></td>
                  <td className={cn("text-center", d)}><div className="flex justify-center"><Avatar user={r.advisorId ? userMap.get(r.advisorId) : undefined} /></div></td>
                  <td className={cn("text-center", d)}><div className="flex justify-center"><Avatar user={r.plId ? userMap.get(r.plId) : undefined} /></div></td>
                  <td className={cn("text-right tabular-nums font-medium", d)}>{formatNOK(r.bfActual)}</td>
                  <td className={cn("text-right tabular-nums text-muted-foreground", d)}>{fmtRate(r.timepris)}</td>
                  <td className={cn("text-right tabular-nums", d)}>{fmtTimer(r.timerSolgt)}</td>
                  <td className={cn("text-right tabular-nums", d)}>{fmtTimer(r.timerBrukt)}</td>
                  <td className={cn("text-right", d)}><AvvikCell pct={r.avvik} /></td>
                  <td className={cn("text-right tabular-nums text-muted-foreground", d)}>{fmtDato(r.dato)}</td>
                  <td className={cn("pr-1 text-right", d)}>
                    <span className="tabular-nums">{formatStorage(r.storageGb)}</span>
                    {r.storageLocation && (
                      <span className="block text-[11px] text-muted-foreground truncate max-w-[110px]" title={r.storageLocation}>
                        {r.storageLocation}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {sorted.length === 0 && (
                <tr><td colSpan={12} className="py-8 text-center text-muted-foreground">Ingen prosjekter</td></tr>
              )}
            </tbody>
            {sorted.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-border font-semibold">
                  <td className={cn("pl-1", d)} colSpan={2}>Sum · {totals.antall} prosj.</td>
                  {/* Salg / Rådgiver / PL har ingen sum. */}
                  <td className={d}></td>
                  <td className={d}></td>
                  <td className={d}></td>
                  <td className={cn("text-right tabular-nums", d)}>{formatNOK(totals.bfActual)}</td>
                  <td className={cn("text-right tabular-nums", d)}>{fmtRate(totals.endeligTimepris)}</td>
                  <td className={cn("text-right tabular-nums", d)}>{fmtTimer(totals.timerSolgt)}</td>
                  <td className={cn("text-right tabular-nums", d)}>{fmtTimer(totals.timerBrukt)}</td>
                  <td className={cn("text-right", d)}><AvvikCell pct={totals.budsjettavvikPct} /></td>
                  <td className={d}></td>
                  {/* Sum GB er meningsfull: det er arkivet vi faktisk betaler for. */}
                  <td className={cn("pr-1 text-right tabular-nums", d)}>{formatStorage(totalStorageGb)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
